import {
  IncidentAffectedAudience,
  IncidentNotificationChannel,
  IncidentNotificationState,
  IncidentStatus,
} from "@no-busy-work/ops-bridge-protocol";
import type { IncidentDetail, IncidentNotificationAttempt } from "@no-busy-work/ops-bridge-protocol";
import {
  AI_QUESTION_KIND,
  AI_SUGGESTION_KINDS,
  CLOSEOUT_GATE,
  CUSTOMER_IMPACT_STATE,
  GRAPH_EDGE_ID_PREFIX,
  GRAPH_EDGE_LABEL,
  GRAPH_HEALTH,
  GRAPH_NODE_ID_PREFIX,
  GRAPH_NODE_KIND,
  INCIDENT_NOTIFICATION_AUDIENCE_REF,
  OPS_ACTION_ENDPOINT_KIND,
  OPS_ACTION_ENDPOINTS,
  OUTAGE_NOTICE_STATE,
  TOOL_PROVIDER_BY_TOOL_ID,
  TOOL_PROVIDER_KIND,
  TOOL_STATE,
  UI_TONE,
  WORKFLOW_STATUS
} from "./OpsCenter.Config";
import { actorRefLabel as formatActorRefLabel } from "./OpsCenter.ActorRefs";
import { formatCopy } from "./OpsCenter.Format";
import { TAB_ID } from "./OpsCenter.Types";
import type { AccountabilityProjection, ActionRow, AiQuestionKind, AiQuestionThread, CommunicationEvidenceRow, GraphEdge, GraphNode, ImpactGraph, OpsActionEndpointKind, OpsCenterCopy, TabId, ToolCatalogEntry, ToolRow, TopologyRow, UiTone } from "./OpsCenter.Types";
import { humanize, statusLabel } from "./i18n/OpsCenter.I18n";

export function actionRows(detail: IncidentDetail | null, copy?: OpsCenterCopy): readonly ActionRow[] {
  if (!detail) {
    return [];
  }
  return detail.recoveryProposals.flatMap((proposal) =>
    proposal.actions.map((action) => {
      const endpointKind =
        action.status === WORKFLOW_STATUS.Blocked
          ? OPS_ACTION_ENDPOINT_KIND.Preview
          : action.kind === "worker_remediation_session"
          ? OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession
          : action.requiresApproval
            ? OPS_ACTION_ENDPOINT_KIND.ApprovalRequest
            : OPS_ACTION_ENDPOINT_KIND.Preview;
      const policyLabel = humanize(action.policyClass);
      return {
        id: action.id,
        proposalId: proposal.id,
        gateId: gateIdForAction(detail, action),
        actionRef: action.actionRef,
        toolId: action.toolId,
        kind: action.kind,
        label: humanize(action.kind),
        summary: action.summary,
        owner: actionOwnerLabel(detail, copy),
        policyClass: action.policyClass,
        policyLabel,
        requiresApproval: action.requiresApproval,
        approvalState: action.approvalState,
        operationHash: action.operationHash,
        status: action.status,
        endpointKind,
        path: actionEndpointPath(detail, endpointKind),
        buttonLabel:
          endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession
            ? copy?.actions.requestSession ?? "Request session"
            : endpointKind === OPS_ACTION_ENDPOINT_KIND.ApprovalRequest
              ? copy?.actions.requestApproval ?? "Request approval"
              : copy?.actions.preview ?? "Preview",
        nextStep: actionNextStep(action.status, endpointKind, action.requiresApproval, copy),
        tone: action.status === WORKFLOW_STATUS.Blocked ? UI_TONE.Critical : action.requiresApproval ? UI_TONE.Warn : UI_TONE.Neutral
      };
    })
  );
}

function actionEndpointPath(detail: IncidentDetail, endpointKind: OpsActionEndpointKind): string {
  if (endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession) {
    return OPS_ACTION_ENDPOINTS[endpointKind];
  }
  const actionIncidentId = firstUuid([detail.incident.id]) ?? nilUuid("2");
  const suffix = endpointKind === OPS_ACTION_ENDPOINT_KIND.Preview ? "/preview" : "";
  return `/api/v1/ops/incidents/${encodeURIComponent(actionIncidentId)}/actions${suffix}`;
}

function gateIdForAction(detail: IncidentDetail, action: IncidentDetail["recoveryProposals"][number]["actions"][number]): string {
  if (action.kind === "worker_remediation_session") {
    return CLOSEOUT_GATE.CodeBridge;
  }
  if (action.kind === "customer_notification") {
    if (detail.communicationSummary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft) {
      return CLOSEOUT_GATE.OutageNotice;
    }
    if (!communicationStateSatisfied(detail.communicationSummary.customerNotificationState)) {
      return CLOSEOUT_GATE.CustomerNotifications;
    }
    return CLOSEOUT_GATE.ResolutionNotifications;
  }
  if (action.requiresApproval) {
    return CLOSEOUT_GATE.ApprovalQueue;
  }
  if (action.kind === "diagnostic_check") {
    return CLOSEOUT_GATE.HealthVerification;
  }
  return CLOSEOUT_GATE.RemediationProof;
}

function communicationStateSatisfied(state: string): boolean {
  return state === IncidentNotificationState.Sent || state === IncidentNotificationState.NotRequired;
}

function firstUuid(values: readonly (string | undefined)[]): string | null {
  return values.find((value) => value !== undefined && isUuid(value)) ?? null;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{12}$/iu.test(value);
}

function nilUuid(suffix: string): string {
  return `00000000-0000-4000-8000-00000000000${suffix}`;
}

function actionNextStep(status: string, endpointKind: OpsActionEndpointKind, requiresApproval: boolean, copy?: OpsCenterCopy): string {
  if (status === WORKFLOW_STATUS.Blocked) {
    return copy?.labels.actionNextPreviewBlockers ?? "Preview blockers";
  }
  if (endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession) {
    return copy?.labels.actionNextCodeBridge ?? "Request Code Bridge handoff";
  }
  if (requiresApproval) {
    return copy?.labels.actionNextApproval ?? "Request approval";
  }
  return copy?.labels.actionNextPreview ?? "Preview safe read";
}

function actionOwnerLabel(detail: IncidentDetail, copy?: OpsCenterCopy): string {
  const ref = detail.incident.accountability?.currentAssigneeRef;
  if (!ref) {
    return copy?.labels.notRecorded ?? "";
  }
  return copy ? actorRefLabel(ref, copy) : humanize(ref.split(":", 2)[1] || ref);
}

const INCIDENT_NOTIFICATION_READY_STATES = new Set<string>([
  IncidentNotificationState.Sent,
  IncidentNotificationState.NotRequired
]);

const INCIDENT_NOTIFICATION_FAILURE_STATES = new Set<string>([
  IncidentNotificationState.Failed,
  IncidentNotificationState.PartiallyFailed
]);

const INCIDENT_NOTIFICATION_WAIT_STATES = new Set<string>([
  IncidentNotificationState.ApprovalRequired,
  IncidentNotificationState.Queued,
  IncidentNotificationState.Sending,
  IncidentNotificationState.Paused
]);

const OUTAGE_NOTICE_READY_STATES = new Set<IncidentDetail["communicationSummary"]["outageNoticeState"]>([
  OUTAGE_NOTICE_STATE.Posted,
  OUTAGE_NOTICE_STATE.NotRequired
]);

export function impactGraph(detail: IncidentDetail, copy: OpsCenterCopy): ImpactGraph {
  const rows = actionRows(detail, copy);
  const actionCount = rows.length;
  const health =
    detail.incident.status === IncidentStatus.Resolved || detail.incident.status === IncidentStatus.Closed
      ? GRAPH_HEALTH.Healthy
      : detail.incident.status === IncidentStatus.Monitoring
        ? GRAPH_HEALTH.Degraded
        : GRAPH_HEALTH.Down;
  const routeRefs = uniqueValues(detail.customerImpact.map((item) => item.affectedRouteRef)).slice(0, 4);
  const customerSegments = customerSegmentRows(detail, copy).slice(0, 4);
  const ownerRefs = uniqueValues([
    ...detail.customerImpact.map((item) => item.ownerRef),
    ...detail.ownerEscalations.map((item) => item.recipientRef),
    detail.incident.accountability.serviceOwnerRef,
    detail.incident.accountability.communicationsOwnerRef,
    detail.incident.accountability.currentAssigneeRef
  ]).slice(0, 3);
  const providerRefs = uniqueValues(rows.map((item) => providerRefForAction(item.toolId ?? item.actionRef))).slice(0, 4);
  const centerRow = Math.max(1, Math.ceil(Math.max(routeRefs.length, customerSegments.length, providerRefs.length, 1) / 2));
  const nodes: GraphNode[] = [
    graphNode(
      GRAPH_NODE_KIND.Incident,
      graphNodeId(GRAPH_NODE_ID_PREFIX.Incident, detail.incident.id),
      copy.labels.currentIncident,
      GRAPH_NODE_KIND.Incident,
      health,
      1,
      centerRow,
      `${detail.incident.severity.toUpperCase()} / ${statusLabel(copy, detail.incident.status)} / ${serviceLabel(detail.incident.serviceId, copy)}`,
      "",
      actionCount,
      copy
    ),
    graphNode(
      GRAPH_NODE_KIND.Service,
      graphNodeId(GRAPH_NODE_ID_PREFIX.Service, detail.incident.serviceId),
      serviceLabel(detail.incident.serviceId, copy),
      GRAPH_NODE_KIND.Service,
      health,
      2,
      centerRow,
      latestSignal(detail) ?? detail.incident.fingerprint,
      detail.incident.affectedAudience === IncidentAffectedAudience.Customers ? copy.labels.customerImpact : "",
      actionCount,
      copy
    )
  ];
  nodes.push(
    ...routeRefs.map((routeRef, index) =>
      graphNode(
        GRAPH_NODE_KIND.Route,
        graphNodeId(GRAPH_NODE_ID_PREFIX.Route, routeRef),
        routeLabel(routeRef, copy),
        GRAPH_NODE_KIND.Route,
        routeHealth(detail, routeRef, health),
        3,
        index + 1,
        routeSummary(detail, routeRef, copy),
        `${customersForRoute(detail, routeRef).length} ${copy.labels.impacted}`,
        customersForRoute(detail, routeRef).length,
        copy
      )
    ),
    ...providerRefs.map((providerRef, index) =>
      graphNode(
        GRAPH_NODE_KIND.Provider,
        graphNodeId(GRAPH_NODE_ID_PREFIX.Provider, providerRef),
        providerLabel(providerRef, copy),
        GRAPH_NODE_KIND.Provider,
        providerHealth(rows, providerRef),
        4,
        index + 1,
        providerSummary(rows, providerRef, copy),
        "",
        rows.filter((item) => providerRefForAction(item.toolId ?? item.actionRef) === providerRef).length,
        copy
      )
    ),
    ...customerSegments.map((segment, index) =>
      graphNode(
        GRAPH_NODE_KIND.CustomerSegment,
        segment.id,
        segment.label,
        GRAPH_NODE_KIND.CustomerSegment,
        segment.health,
        5,
        index + 1,
        segment.summary,
        segment.badge,
        segment.actionCount,
        copy
      )
    ),
    ...ownerRefs.map((ownerRef, index) =>
      graphNode(
        GRAPH_NODE_KIND.Owner,
        graphNodeId(GRAPH_NODE_ID_PREFIX.Owner, ownerRef),
        actorRefLabel(ownerRef, copy),
        GRAPH_NODE_KIND.Owner,
        detail.communicationSummary.ownerAcknowledged ? GRAPH_HEALTH.Healthy : GRAPH_HEALTH.Unknown,
        2,
        centerRow + index + 1,
        detail.communicationSummary.ownerAcknowledged ? copy.status.acknowledged : copy.labels.ownerPending,
        "",
        detail.ownerEscalations.length,
        copy
      )
    )
  );
  const incidentNode = nodes[0];
  const serviceNode = nodes[1];
  const routeNodes = nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.Route);
  const providerNodes = nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.Provider);
  const customerSegmentNodes = nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.CustomerSegment);
  const ownerNodes = nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.Owner);
  const edges: GraphEdge[] = [
    graphEdge(graphEdgeId("incident-service"), incidentNode, serviceNode, GRAPH_EDGE_LABEL.Observes, copy),
    ...routeNodes.map((route) => graphEdge(graphEdgeId(`service-${route.id}`), serviceNode, route, GRAPH_EDGE_LABEL.Routes, copy)),
    ...providerNodes.map((provider) => graphEdge(graphEdgeId(`${provider.id}-service`), provider, serviceNode, GRAPH_EDGE_LABEL.Recovers, copy)),
    ...ownerNodes.map((owner) => graphEdge(graphEdgeId(`${owner.id}-service`), owner, serviceNode, GRAPH_EDGE_LABEL.Owns, copy)),
    ...customerSegmentNodes.map((segmentNode) => {
      const segment = customerSegments.find((item) => item.id === segmentNode.id);
      const route = routeNodes.find((item) => item.id === graphNodeId(GRAPH_NODE_ID_PREFIX.Route, segment?.routeRef ?? "")) ?? serviceNode;
      return graphEdge(graphEdgeId(`${route.id}-${segmentNode.id}`), route, segmentNode, GRAPH_EDGE_LABEL.Impacts, copy);
    })
  ];
  return {
    nodes,
    edges
  };
}

export function graphFocusRows(graph: ImpactGraph, focusId: string, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  const node = graph.nodes.find((item) => item.id === focusId) ?? graph.nodes[1];
  if (!node) {
    return [];
  }
  const connectedEdges = graph.edges.filter((edge) => edge.from === node.id || edge.to === node.id);
  return [
    [node.label, `${node.roleLabel} / ${statusLabel(copy, node.health)} / ${node.summary}`],
    [copy.labels.connectionPoints, connectedEdges.map((edge) => edge.summary).join(" / ") || copy.empty.noneRecorded],
    [copy.labels.action, node.actionCount ? String(node.actionCount) : copy.labels.none]
  ];
}

export function topologyRows(graph: ImpactGraph, copy: OpsCenterCopy): readonly TopologyRow[] {
  return graph.edges.map((edge) => ({
    id: edge.id,
    label: edge.displayLabel,
    health: edge.health,
    summary: edge.summary,
    meta: statusLabel(copy, edge.health)
  }));
}

export function toolRows(detail: IncidentDetail | null, healthOk: boolean, catalog: readonly ToolCatalogEntry[] = []): readonly ToolRow[] {
  const involved = incidentToolIds(detail);
  const sourceCatalog = catalog.length > 0 ? catalog : fallbackToolCatalog();
  const rows = sourceCatalog.map((tool, index) => ({
    id: tool.id,
    label: tool.label,
    provider: tool.provider,
    owner: tool.owner,
    environmentScope: tool.environmentScope,
    actionCount: tool.actionCount,
    requiresApproval: tool.requiresApproval,
    ready: tool.ready,
    state: toolState(tool, involved, detail, healthOk),
    index
  }));
  for (const toolId of involved) {
    if (!rows.some((row) => row.id === toolId)) {
      rows.push({
        id: toolId,
        label: humanize(toolId),
        provider: TOOL_PROVIDER_KIND.Observability,
        owner: "",
        environmentScope: [],
        actionCount: 0,
        requiresApproval: false,
        ready: true,
        state: TOOL_STATE.Involved,
        index: rows.length
      });
    }
  }
  return rows
    .sort((left, right) => toolStateRank(left.state) - toolStateRank(right.state) || left.index - right.index)
    .map((row) => ({
      id: row.id,
      label: row.label,
      provider: row.provider,
      owner: row.owner,
      environmentScope: row.environmentScope,
      actionCount: row.actionCount,
      requiresApproval: row.requiresApproval,
      ready: row.ready,
      state: row.state
    }));
}

function incidentToolIds(detail: IncidentDetail | null): ReadonlySet<string> {
  const toolIds = new Set(detail?.incident.runbook?.toolIds ?? []);
  for (const proposal of detail?.recoveryProposals ?? []) {
    for (const action of proposal.actions) {
      if (action.toolId) {
        toolIds.add(action.toolId);
      }
    }
  }
  return toolIds;
}

function toolState(tool: ToolCatalogEntry, involved: ReadonlySet<string>, detail: IncidentDetail | null, healthOk: boolean): string {
  if (!healthOk || !tool.ready || (tool.provider === TOOL_PROVIDER_KIND.Llm && detail?.incident.manualFallbackRequired)) {
    return WORKFLOW_STATUS.Blocked;
  }
  return involved.has(tool.id) ? TOOL_STATE.Involved : TOOL_STATE.Available;
}

function toolStateRank(state: string): number {
  if (state === TOOL_STATE.Involved) {
    return 0;
  }
  if (state === WORKFLOW_STATUS.Blocked) {
    return 1;
  }
  return 2;
}

function fallbackToolCatalog(): readonly ToolCatalogEntry[] {
  return Object.entries(TOOL_PROVIDER_BY_TOOL_ID).map(([id, provider]) => ({
    id,
    label: humanize(id),
    provider,
    owner: "",
    environmentScope: [],
    actionCount: 0,
    requiresApproval: false,
    ready: true
  }));
}

export function recoveryRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.recoveryProposals.flatMap((proposal) => [
    [proposal.advisor.toUpperCase(), proposal.summary] as const,
    ...proposal.actions.map((action) => [
      action.requiresApproval ? copy.labels.approvalRequired : copy.labels.action,
      `${humanize(action.kind)}: ${action.summary}`
    ] as const)
  ]);
}

export function notificationRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.notifications.map((item) => [
    humanize(item.channel),
    [
      humanize(item.template),
      statusLabel(copy, item.state),
      statusLabel(copy, item.approvalState),
      item.sentAt ? `${copy.labels.sentAt} ${formatTime(item.sentAt)}` : copy.labels.notRecorded
    ].join(" / ")
  ] as const);
}

export function communicationEvidenceRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly CommunicationEvidenceRow[] {
  const summary = detail.communicationSummary;
  const statusPageNotifications = detail.notifications.filter(isStatusPageNotification);
  const customerNotifications = detail.notifications.filter((item) => !isStatusPageNotification(item) && !isOwnerAudienceRef(item.audienceRef));
  const statusPageSentAt = latestTimestamp(statusPageNotifications.map((item) => item.sentAt));
  const customerSentCount = customerNotifications.filter((item) => item.state === IncidentNotificationState.Sent).length;
  const customerFailedCount = customerNotifications.filter((item) => INCIDENT_NOTIFICATION_FAILURE_STATES.has(item.state)).length;
  const customerQueuedCount = customerNotifications.filter((item) => INCIDENT_NOTIFICATION_WAIT_STATES.has(item.state)).length;
  const linkedComplaintCount = detail.complainingCustomers.filter((item) => item.linked).reduce((sum, item) => sum + item.complaintCount, 0);
  const unlinkedComplaintCount = detail.complainingCustomers.filter((item) => !item.linked).reduce((sum, item) => sum + item.complaintCount, 0);
  const newestComplaintAt = latestTimestamp(detail.complainingCustomers.map((item) => item.newestAt));
  const ownerEscalation = detail.ownerEscalations[0] ?? null;
  const resolutionSentCount = detail.resolutionNotifications.filter((item) => item.sentAt || item.state === IncidentNotificationState.Sent).length;
  const resolutionDeliveredCount = detail.resolutionNotifications.filter((item) => item.deliveredAt || item.state === IncidentNotificationState.Sent).length;
  const resolutionFailedCount = detail.resolutionNotifications.filter((item) => INCIDENT_NOTIFICATION_FAILURE_STATES.has(item.state)).length;
  const resolutionQueuedCount = detail.resolutionNotifications.filter((item) => INCIDENT_NOTIFICATION_WAIT_STATES.has(item.state)).length;
  const latestResolutionSentAt = latestTimestamp(detail.resolutionNotifications.map((item) => item.sentAt));
  const latestResolutionDeliveredAt = latestTimestamp(detail.resolutionNotifications.map((item) => item.deliveredAt));
  return [
    {
      id: "outage-notice",
      title: copy.labels.outageNotice,
      state: statusLabel(copy, summary.outageNoticeState),
      tone: outageNoticeTone(summary.outageNoticeState),
      rows: [
        [copy.labels.statusPage, summary.statusPagePosted ? copy.status.posted : copy.status.pending],
        [copy.labels.actionState, statusLabel(copy, statusPageNotifications[0]?.approvalState ?? summary.outageNoticeState)],
        [copy.labels.sentAt, statusPageSentAt ? formatTime(statusPageSentAt) : copy.labels.notRecorded],
        [copy.labels.audiences, summarizeValues(statusPageNotifications.map((item) => item.audienceRef), copy)]
      ]
    },
    {
      id: "customer-notifications",
      title: copy.labels.customerNotifications,
      state: statusLabel(copy, summary.customerNotificationState),
      tone: notificationTone(summary.customerNotificationState),
      rows: [
        [copy.labels.audiences, summarizeValues(customerNotifications.map((item) => item.audienceRef), copy)],
        [copy.labels.sent, String(customerSentCount)],
        [copy.labels.deliveryFailures, String(customerFailedCount)],
        [copy.labels.queuedDeliveries, String(customerQueuedCount)]
      ]
    },
    {
      id: "complaint-intake",
      title: copy.sections.complaintIntake,
      state: summary.complaintCount > 0 ? copy.status.open : copy.status.clear,
      tone: summary.complaintCount > 0 ? UI_TONE.Warn : UI_TONE.Ok,
      rows: [
        [copy.labels.complaints, String(summary.complaintCount)],
        [copy.labels.linkedComplaints, String(linkedComplaintCount)],
        [copy.labels.unmatchedComplaints, String(unlinkedComplaintCount)],
        [copy.labels.newestComplaint, newestComplaintAt ? formatTime(newestComplaintAt) : copy.labels.notRecorded]
      ]
    },
    {
      id: "owner-acknowledgement",
      title: copy.labels.ownerAcknowledgement,
      state: summary.ownerAcknowledged ? copy.status.acknowledged : copy.status.pending,
      tone: summary.ownerAcknowledged ? UI_TONE.Ok : UI_TONE.Warn,
      rows: [
        [copy.labels.owner, ownerEscalation ? humanize(ownerEscalation.recipientRef.replace("owner:", "")) : copy.labels.notRecorded],
        [copy.labels.actionState, ownerEscalation ? statusLabel(copy, ownerEscalation.state) : copy.status.pending],
        [copy.labels.due, ownerEscalation?.dueAt ? formatTime(ownerEscalation.dueAt) : copy.labels.notRecorded],
        [copy.labels.channel, ownerEscalation ? humanize(ownerEscalation.channel) : copy.labels.notRecorded]
      ]
    },
    {
      id: "resolution-notifications",
      title: copy.labels.resolutionNotifications,
      state: statusLabel(copy, summary.resolutionNotificationState),
      tone: summary.deliveryRiskCount > 0 ? UI_TONE.Warn : notificationTone(summary.resolutionNotificationState),
      rows: [
        [copy.labels.sent, String(resolutionSentCount)],
        [copy.labels.delivered, String(resolutionDeliveredCount)],
        [copy.labels.deliveryFailures, String(resolutionFailedCount)],
        [copy.labels.queuedDeliveries, String(resolutionQueuedCount)]
      ]
    },
    {
      id: "resolution-delivery-receipts",
      title: copy.labels.resolutionDelivery,
      state: summary.deliveryRiskCount > 0 ? copy.status.failed : copy.status.clear,
      tone: summary.deliveryRiskCount > 0 ? UI_TONE.Danger : UI_TONE.Ok,
      rows: [
        [copy.labels.deliveryRisks, String(summary.deliveryRiskCount)],
        [copy.labels.sentAt, latestResolutionSentAt ? formatTime(latestResolutionSentAt) : copy.labels.notRecorded],
        [copy.labels.deliveredAt, latestResolutionDeliveredAt ? formatTime(latestResolutionDeliveredAt) : copy.labels.notRecorded],
        [copy.labels.receipts, String(detail.resolutionNotifications.length)]
      ]
    }
  ];
}

export function communicationSummaryRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  const summary = detail.communicationSummary;
  return [
    [copy.labels.impactedCustomers, String(summary.impactedCustomerCount)],
    [copy.labels.enterpriseCustomers, String(summary.enterpriseCustomerCount)],
    [copy.labels.complaints, String(summary.complaintCount)],
    [copy.labels.outageNotice, statusLabel(copy, summary.outageNoticeState)],
    [copy.labels.customerNotifications, statusLabel(copy, summary.customerNotificationState)],
    [copy.labels.resolutionNotifications, statusLabel(copy, summary.resolutionNotificationState)],
    [copy.labels.ownerAcknowledgement, summary.ownerAcknowledged ? copy.status.acknowledged : copy.status.pending],
    [copy.labels.deliveryRisks, String(summary.deliveryRiskCount)]
  ];
}

export function actorRefLabel(ref: string | null | undefined, copy: OpsCenterCopy): string {
  return formatActorRefLabel(ref, copy);
}

export function accountabilityProjection(detail: IncidentDetail, copy: OpsCenterCopy): AccountabilityProjection {
  const accountability = detail.incident.accountability;
  return {
    incidentCommander: actorRefLabel(accountability.incidentCommanderRef, copy),
    serviceOwner: actorRefLabel(accountability.serviceOwnerRef, copy),
    communicationsOwner: actorRefLabel(accountability.communicationsOwnerRef, copy),
    currentAssignee: actorRefLabel(accountability.currentAssigneeRef, copy),
    handoffState: statusLabel(copy, accountability.handoffState),
    ownerAcknowledgedAt: accountability.ownerAcknowledgedAt
      ? formatTime(accountability.ownerAcknowledgedAt)
      : detail.communicationSummary.ownerAcknowledged
        ? copy.status.acknowledged
        : copy.status.pending
  };
}

export function accountabilityRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  const projection = accountabilityProjection(detail, copy);
  return [
    [copy.labels.incidentCommander, projection.incidentCommander],
    [copy.labels.serviceOwner, projection.serviceOwner],
    [copy.labels.communicationsOwner, projection.communicationsOwner],
    [copy.labels.currentAssignee, projection.currentAssignee],
    [copy.labels.handoffState, projection.handoffState],
    [copy.labels.ownerAcknowledgedAt, projection.ownerAcknowledgedAt]
  ];
}

export function lifecycleRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  const lifecycle = detail.incident.lifecycle;
  return [
    [copy.labels.detectedAt, formatTime(lifecycle.detectedAt)],
    [copy.labels.acknowledgedAt, lifecycle.acknowledgedAt ? formatTime(lifecycle.acknowledgedAt) : copy.labels.notRecorded],
    [copy.labels.mitigatedAt, lifecycle.mitigatedAt ? formatTime(lifecycle.mitigatedAt) : copy.labels.notRecorded],
    [copy.labels.monitoringAt, lifecycle.monitoringAt ? formatTime(lifecycle.monitoringAt) : copy.labels.notRecorded],
    [copy.labels.resolvedAt, lifecycle.resolvedAt ? formatTime(lifecycle.resolvedAt) : copy.labels.notRecorded],
    [copy.labels.lastTransitionAt, formatTime(lifecycle.lastTransitionAt)]
  ];
}

export function customerImpactRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.customerImpact.map((customer) => [
    customer.displayName,
    [
      statusLabel(copy, customer.tier),
      serviceLabel(customer.affectedServiceRef, copy),
      humanize(customer.affectedRouteRef),
      statusLabel(copy, customer.state),
      `${customer.complaintCount} ${copy.labels.complaints}`
    ].join(" / ")
  ] as const);
}

export function complaintRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.complainingCustomers.map((complaint) => [
    complaint.displayName,
    [
      humanize(complaint.source),
      formatTime(complaint.newestAt),
      `${complaint.complaintCount} ${copy.labels.complaints}`,
      complaint.linked ? copy.status.linked : copy.status.review
    ].join(" / ")
  ] as const);
}

export function resolutionNotificationRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.resolutionNotifications.map((receipt) => [
    receipt.displayName,
    [
      humanize(receipt.channel),
      statusLabel(copy, receipt.state),
      receipt.sentAt ? `${copy.labels.sent} ${formatTime(receipt.sentAt)}` : copy.status.pending,
      receipt.deliveredAt ? `${copy.labels.delivered} ${formatTime(receipt.deliveredAt)}` : copy.labels.deliveryPending
    ].join(" / ")
  ] as const);
}

export function customerImpactBandRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly { readonly label: string; readonly tone: UiTone }[] {
  const summary = detail.communicationSummary;
  return [
    { label: `${summary.impactedCustomerCount} ${copy.labels.impacted}`, tone: summary.impactedCustomerCount > 0 ? UI_TONE.Warn : UI_TONE.Ok },
    { label: `${summary.complaintCount} ${copy.labels.complaints}`, tone: summary.complaintCount > 0 ? UI_TONE.Danger : UI_TONE.Ok },
    { label: `${copy.labels.notice}: ${statusLabel(copy, summary.outageNoticeState)}`, tone: summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft ? UI_TONE.Warn : UI_TONE.Ok },
    { label: `${copy.labels.resolution}: ${statusLabel(copy, summary.resolutionNotificationState)}`, tone: summary.deliveryRiskCount > 0 ? UI_TONE.Warn : UI_TONE.Ok },
    { label: summary.ownerAcknowledged ? copy.labels.ownerAcked : copy.labels.ownerPending, tone: summary.ownerAcknowledged ? UI_TONE.Ok : UI_TONE.Warn },
    { label: `${summary.deliveryRiskCount} ${copy.labels.deliveryRisks}`, tone: summary.deliveryRiskCount > 0 ? UI_TONE.Warn : UI_TONE.Ok }
  ];
}

export function resolutionRows(detail: IncidentDetail, rootCause: string, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [copy.labels.rootCause, detail.incident.resolvedAt ? rootCause : copy.labels.pending],
    [copy.labels.remediationProof, detail.timeline.find((item) => item.kind === "remediation_session_requested")?.redactedBody ?? copy.labels.pending],
    [copy.labels.customerSafeSummary, detail.notifications.length > 0 ? copy.labels.notificationApprovalQueued : copy.labels.noCustomerNotice]
  ];
}

export function learningRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.learningRecords.map((record) => [
    record.title,
    [
      statusLabel(copy, record.kind),
      record.summary,
      `${copy.labels.evidence}: ${record.evidenceRefs.length}`,
      `${copy.labels.owner}: ${actorRefLabel(record.createdByRef, copy)}`
    ].join(" / ")
  ] as const);
}

export function capabilityProposalRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return detail.capabilityProposals.map((proposal) => [
    proposal.title,
    [
      statusLabel(copy, proposal.kind),
      statusLabel(copy, proposal.state),
      `${copy.labels.action}: ${proposal.proposedActionRef}`,
      proposal.replayRequired ? copy.labels.replayRequired : copy.labels.replayNotRequired,
      proposal.reviewRequired ? copy.labels.reviewRequired : copy.labels.reviewNotRequired
    ].join(" / ")
  ] as const);
}

export type { ResolutionReadinessProjection, ResolutionReadinessRow } from "./OpsCenter.Workflow";
export { activeTaskProjection, closeoutGateIsInteractive, closeoutWorkflowRows, resolutionReadiness, responderRows } from "./OpsCenter.Workflow";
export function aiContextRows(detail: IncidentDetail | null, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  if (!detail) {
    return [[copy.status.pending, copy.empty.noIncidentSelected]];
  }
  return [
    [copy.labels.currentIncident, `${detail.incident.severity.toUpperCase()} ${serviceLabel(detail.incident.serviceId, copy)}`],
    [copy.labels.impactedCustomers, String(detail.communicationSummary.impactedCustomerCount)],
    [copy.labels.complaints, String(detail.communicationSummary.complaintCount)],
    [copy.labels.runbook, detail.incident.runbook?.title ?? copy.labels.manualTriage],
    [copy.sections.approvalQueue, String((detail.decisions?.length ?? 0) + (detail.ownerEscalations?.length ?? 0))],
    [copy.sections.evidence, String(detail.evidence.length)]
  ];
}

interface AiSuggestionRow {
  readonly kind: AiQuestionKind;
  readonly label: string;
  readonly summary: string;
  readonly tone: UiTone;
}

interface AiPanelFollowUpRow {
  readonly id: string;
  readonly kind: AiQuestionKind;
  readonly label: string;
  readonly summary: string;
  readonly tone: UiTone;
}

export function aiSuggestedQuestions(detail: IncidentDetail | null, copy: OpsCenterCopy): readonly AiSuggestionRow[] {
  return AI_SUGGESTION_KINDS.map((kind) => ({
    kind,
    label: copy.orchestrator[`suggestion.${kind}`] ?? humanize(kind),
    summary: aiSuggestionSummary(kind, detail, copy),
    tone: aiQuestionTone(kind, detail, copy)
  }));
}

export function buildAiQuestionThread(question: string, kind: AiQuestionKind, detail: IncidentDetail | null, copy: OpsCenterCopy, sequence: number): AiQuestionThread {
  return {
    id: `ai:${sequence}:${kind}`,
    kind,
    question,
    answer: aiAnswer(kind, detail, copy),
    followUps: aiFollowUps(kind, detail, copy)
  };
}

export function aiPanelFollowUps(detail: IncidentDetail | null, copy: OpsCenterCopy): readonly AiPanelFollowUpRow[] {
  const summary = detail?.communicationSummary;
  const hasCodeBridge = Boolean(detail && actionRows(detail, copy).some((row) => row.endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession));
  return [
    {
      id: "customer-safe-summary",
      kind: AI_QUESTION_KIND.Comms,
      label: copy.orchestrator["followUp.customerSummary"],
      summary: summary
        ? formatCopy(copy.orchestrator["followUp.customerSummary.summary"], {
            notice: statusLabel(copy, summary.outageNoticeState),
            resolution: statusLabel(copy, summary.resolutionNotificationState)
          })
        : copy.empty.selectIncident,
      tone: summary?.outageNoticeState === OUTAGE_NOTICE_STATE.Draft || summary?.deliveryRiskCount ? UI_TONE.Warn : UI_TONE.Ok
    },
    {
      id: "complaint-spike",
      kind: AI_QUESTION_KIND.Customers,
      label: copy.orchestrator["followUp.complaints"],
      summary: summary
        ? formatCopy(copy.orchestrator["followUp.complaints.summary"], {
            complaints: String(summary.complaintCount),
            impacted: String(summary.impactedCustomerCount)
          })
        : copy.empty.selectIncident,
      tone: summary && summary.complaintCount > 0 ? UI_TONE.Danger : UI_TONE.Neutral
    },
    {
      id: "code-bridge-waiting",
      kind: AI_QUESTION_KIND.Tools,
      label: copy.orchestrator["followUp.codeBridge"],
      summary: hasCodeBridge ? copy.orchestrator["followUp.codeBridge.waiting"] : copy.orchestrator["followUp.codeBridge.clear"],
      tone: hasCodeBridge ? UI_TONE.Warn : UI_TONE.Neutral
    },
    {
      id: "replay-evidence",
      kind: AI_QUESTION_KIND.Fingerprint,
      label: copy.orchestrator["followUp.replay"],
      summary: detail ? copy.orchestrator["followUp.replay.summary"] : copy.empty.selectIncident,
      tone: UI_TONE.Neutral
    },
    {
      id: "capability-proposal",
      kind: AI_QUESTION_KIND.Runbook,
      label: copy.orchestrator["followUp.capability"],
      summary: detail ? copy.orchestrator["followUp.capability.summary"] : copy.empty.selectIncident,
      tone: detail?.incident.manualFallbackRequired ? UI_TONE.Warn : UI_TONE.Neutral
    }
  ];
}

export function aiGuardrailRows(copy: OpsCenterCopy): readonly (readonly [string, string, UiTone])[] {
  return [
    [copy.orchestrator.guardrailExternalWrites, copy.orchestrator.guardrailExternalWritesBody, UI_TONE.Critical],
    [copy.orchestrator.guardrailRedaction, copy.orchestrator.guardrailRedactionBody, UI_TONE.Warn],
    [copy.orchestrator.guardrailPolicy, copy.orchestrator.guardrailPolicyBody, UI_TONE.Neutral]
  ];
}

export function resolveAiQuestionKind(question: string): AiQuestionKind {
  const normalized = question.toLowerCase();
  if (normalized.includes("customer") || normalized.includes("complaint") || normalized.includes("impacted") || normalized.includes("cliente") || normalized.includes("klient")) {
    return AI_QUESTION_KIND.Customers;
  }
  if (normalized.includes("notice") || normalized.includes("posted") || normalized.includes("status page") || normalized.includes("notifi")) {
    return AI_QUESTION_KIND.Notice;
  }
  if (normalized.includes("block") || normalized.includes("waiting") || normalized.includes("resolution")) {
    return AI_QUESTION_KIND.Blockers;
  }
  if (normalized.includes("tool") || normalized.includes("provider") || normalized.includes("mcp") || normalized.includes("instrument")) {
    return AI_QUESTION_KIND.Tools;
  }
  if (normalized.includes("runbook") || normalized.includes("capability") || normalized.includes("vozmozh")) {
    return AI_QUESTION_KIND.Runbook;
  }
  if (normalized.includes("fingerprint") || normalized.includes("match") || normalized.includes("prior") || normalized.includes("replay")) {
    return AI_QUESTION_KIND.Fingerprint;
  }
  if (normalized.includes("approval") || normalized.includes("aprob") || normalized.includes("odobren")) {
    return AI_QUESTION_KIND.Approvals;
  }
  if (normalized.includes("evidence") || normalized.includes("handoff") || normalized.includes("eviden") || normalized.includes("dokaz")) {
    return AI_QUESTION_KIND.Evidence;
  }
  if (normalized.includes("comms")) {
    return AI_QUESTION_KIND.Comms;
  }
  return AI_QUESTION_KIND.Triage;
}

export function latestSignal(detail: IncidentDetail): string | null {
  return detail.signals[detail.signals.length - 1]?.redactedSummary ?? null;
}

function aiAnswer(kind: AiQuestionKind, detail: IncidentDetail | null, copy: OpsCenterCopy): string {
  if (!detail) {
    return copy.orchestrator.answerNoIncident;
  }
  if (detail.incident.manualFallbackRequired) {
    return copy.orchestrator.manualFallback;
  }
  if (kind === AI_QUESTION_KIND.Customers) {
    const summary = detail.communicationSummary;
    return formatCopy(copy.orchestrator.answerCustomers, {
      impacted: String(summary.impactedCustomerCount),
      enterprise: String(summary.enterpriseCustomerCount),
      complaints: String(summary.complaintCount),
      customers: customerNames(detail, copy)
    });
  }
  if (kind === AI_QUESTION_KIND.Notice) {
    const summary = detail.communicationSummary;
    return formatCopy(copy.orchestrator.answerNotice, {
      notice: statusLabel(copy, summary.outageNoticeState),
      posted: summary.statusPagePosted ? copy.status.posted : copy.status.pending,
      resolution: statusLabel(copy, summary.resolutionNotificationState)
    });
  }
  if (kind === AI_QUESTION_KIND.Blockers) {
    return formatCopy(copy.orchestrator.answerBlockers, {
      blockers: aiBlockerLabels(detail, copy).join("; ")
    });
  }
  if (kind === AI_QUESTION_KIND.Tools) {
    const tools = involvedToolNames(detail, copy);
    return formatCopy(copy.orchestrator.answerTools, {
      count: String(tools.length),
      tools: tools.join(", ") || copy.empty.noneRecorded
    });
  }
  if (kind === AI_QUESTION_KIND.Runbook) {
    return formatCopy(copy.orchestrator.answerRunbook, {
      runbook: detail.incident.runbook?.title ?? copy.labels.manualTriage,
      fallback: detail.incident.runbook?.manualFallbackAvailable ? copy.status.available : copy.status.blocked
    });
  }
  if (kind === AI_QUESTION_KIND.Fingerprint) {
    return formatCopy(copy.orchestrator.answerFingerprint, {
      fingerprint: detail.incident.fingerprint
    });
  }
  if (kind === AI_QUESTION_KIND.Approvals) {
    const count = (detail.decisions?.length ?? 0) + (detail.ownerEscalations?.length ?? 0);
    return `${copy.orchestrator.answerApprovalsPrefix} ${count}. ${count > 0 ? copy.orchestrator.answerApprovalsWaiting : copy.orchestrator.answerApprovalsClear}`;
  }
  if (kind === AI_QUESTION_KIND.Evidence) {
    return `${copy.orchestrator.answerEvidencePrefix} ${detail.evidence.length}. ${copy.orchestrator.answerEvidenceSuffix}`;
  }
  if (kind === AI_QUESTION_KIND.Comms) {
    const summary = detail.communicationSummary;
    return `${copy.orchestrator.answerCommsPrefix} ${summary.impactedCustomerCount}. ${copy.labels.complaints}: ${summary.complaintCount}. ${copy.labels.deliveryRisks}: ${summary.deliveryRiskCount}.`;
  }
  return `${copy.orchestrator.answerTriagePrefix} ${detail.incident.severity.toUpperCase()} ${serviceLabel(detail.incident.serviceId, copy)}. ${copy.orchestrator.answerTriageSuffix}`;
}

function aiFollowUps(kind: AiQuestionKind, detail: IncidentDetail | null, copy: OpsCenterCopy): readonly string[] {
  if (kind === AI_QUESTION_KIND.Customers) {
    return [copy.orchestrator.followUpComms, copy.orchestrator["followUp.complaints"]];
  }
  if (kind === AI_QUESTION_KIND.Notice || kind === AI_QUESTION_KIND.Blockers) {
    return [copy.orchestrator.followUpApprovals, copy.orchestrator.followUpComms];
  }
  if (kind === AI_QUESTION_KIND.Tools || kind === AI_QUESTION_KIND.Runbook || kind === AI_QUESTION_KIND.Fingerprint) {
    return aiPanelFollowUps(detail, copy).slice(2, 5).map((row) => row.label);
  }
  if (kind === AI_QUESTION_KIND.Approvals) {
    return [copy.orchestrator.followUpEvidence, copy.orchestrator.followUpComms];
  }
  if (kind === AI_QUESTION_KIND.Evidence) {
    return [copy.orchestrator.followUpApprovals, copy.orchestrator.followUpTriage];
  }
  if (kind === AI_QUESTION_KIND.Comms) {
    return [copy.orchestrator.followUpApprovals, copy.orchestrator.followUpEvidence];
  }
  return [copy.orchestrator.followUpApprovals, copy.orchestrator.followUpEvidence];
}

function aiSuggestionSummary(kind: AiQuestionKind, detail: IncidentDetail | null, copy: OpsCenterCopy): string {
  if (!detail) {
    return copy.empty.selectIncident;
  }
  const summary = detail.communicationSummary;
  if (kind === AI_QUESTION_KIND.Customers) {
    return formatCopy(copy.orchestrator["suggestionSummary.customers"], {
      impacted: String(summary.impactedCustomerCount),
      complaints: String(summary.complaintCount)
    });
  }
  if (kind === AI_QUESTION_KIND.Notice) {
    return formatCopy(copy.orchestrator["suggestionSummary.notice"], {
      notice: statusLabel(copy, summary.outageNoticeState),
      resolution: statusLabel(copy, summary.resolutionNotificationState)
    });
  }
  if (kind === AI_QUESTION_KIND.Blockers) {
    return formatCopy(copy.orchestrator["suggestionSummary.blockers"], {
      count: String(activeAiBlockerLabels(detail, copy).length)
    });
  }
  if (kind === AI_QUESTION_KIND.Tools) {
    return formatCopy(copy.orchestrator["suggestionSummary.tools"], {
      count: String(involvedToolNames(detail, copy).length)
    });
  }
  if (kind === AI_QUESTION_KIND.Runbook) {
    return detail.incident.runbook?.title ?? copy.labels.manualTriage;
  }
  if (kind === AI_QUESTION_KIND.Fingerprint) {
    return detail.incident.fingerprint;
  }
  return copy.orchestrator.subtitle;
}

function aiQuestionTone(kind: AiQuestionKind, detail: IncidentDetail | null, copy: OpsCenterCopy): UiTone {
  if (!detail) {
    return UI_TONE.Neutral;
  }
  const summary = detail.communicationSummary;
  if (kind === AI_QUESTION_KIND.Customers && summary.complaintCount > 0) {
    return UI_TONE.Danger;
  }
  if (kind === AI_QUESTION_KIND.Notice && (summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft || !summary.statusPagePosted)) {
    return UI_TONE.Warn;
  }
  if (kind === AI_QUESTION_KIND.Blockers && activeAiBlockerLabels(detail, copy).length > 0) {
    return UI_TONE.Warn;
  }
  if (kind === AI_QUESTION_KIND.Tools && involvedToolNames(detail, copy).length > 0) {
    return UI_TONE.Ok;
  }
  return UI_TONE.Neutral;
}

function aiBlockerLabels(detail: IncidentDetail, copy: OpsCenterCopy): readonly string[] {
  const blockers = activeAiBlockerLabels(detail, copy);
  return blockers.length ? blockers : [copy.status.clear];
}

function activeAiBlockerLabels(detail: IncidentDetail, copy: OpsCenterCopy): readonly string[] {
  const summary = detail.communicationSummary;
  const blockers: string[] = [];
  if (summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft) {
    blockers.push(copy.labels.outageNoticeApproval);
  }
  if (summary.deliveryRiskCount > 0) {
    blockers.push(`${summary.deliveryRiskCount} ${copy.labels.deliveryRisks}`);
  }
  if (!summary.ownerAcknowledged) {
    blockers.push(copy.labels.ownerPending);
  }
  const approvalCount = (detail.decisions?.length ?? 0) + (detail.ownerEscalations?.length ?? 0);
  if (approvalCount > 0) {
    blockers.push(`${approvalCount} ${copy.sections.approvalQueue}`);
  }
  if (actionRows(detail, copy).some((row) => row.endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession)) {
    blockers.push(copy.sections.codeBridge);
  }
  return blockers;
}

function involvedToolNames(detail: IncidentDetail, copy: OpsCenterCopy): readonly string[] {
  return toolRows(detail, true, [])
    .filter((tool) => tool.state === TOOL_STATE.Involved)
    .map((tool) => copy.toolLabels[tool.id] ?? tool.label)
    .slice(0, 6);
}

function customerNames(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const names = detail.customerImpact.slice(0, 4).map((customer) => customer.displayName);
  return names.length ? names.join(", ") : copy.empty.noneRecorded;
}

export function serviceLabel(serviceId: string, copy?: OpsCenterCopy): string {
  return copy?.serviceLabels[serviceId] ?? humanize(serviceId);
}

export function routeLabel(routeRef: string, copy?: OpsCenterCopy): string {
  return copy?.routeLabels[routeRef] ?? humanize(routeRef);
}

export function statusTone(status: string): UiTone {
  if (status === IncidentStatus.Resolved || status === IncidentStatus.Closed) {
    return UI_TONE.Ok;
  }
  if (status === IncidentStatus.Monitoring) {
    return UI_TONE.Neutral;
  }
  return UI_TONE.Warn;
}

export function severityTone(severity: string): UiTone {
  return severity === "sev0" || severity === "sev1" ? UI_TONE.Danger : UI_TONE.Warn;
}

export function notificationTone(value: string): UiTone {
  if (INCIDENT_NOTIFICATION_READY_STATES.has(value)) {
    return UI_TONE.Ok;
  }
  if (INCIDENT_NOTIFICATION_FAILURE_STATES.has(value)) {
    return UI_TONE.Danger;
  }
  return UI_TONE.Warn;
}

export function slaSeconds(detail: IncidentDetail): { readonly elapsed: number; readonly remaining: number } {
  const elapsed = Math.max(0, Math.floor((Date.now() - Date.parse(detail.incident.createdAt)) / 1000));
  return { elapsed, remaining: 600 - elapsed };
}

export function slaLabel(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const seconds = slaSeconds(detail);
  if (seconds.remaining < 0) {
    return copy.status.breached;
  }
  return `${Math.ceil(seconds.remaining / 60)} min`;
}

export function affectedRoutes(detail: IncidentDetail): readonly string[] {
  return [...new Set(detail.signals.map((item) => item.serviceId))];
}

function uniqueValues(values: readonly string[]): readonly string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}

function summarizeValues(values: readonly string[], copy: OpsCenterCopy): string {
  const unique = uniqueValues(values).map(humanize);
  return unique.length ? unique.join(", ") : copy.labels.notRecorded;
}

function latestTimestamp(values: readonly (string | null | undefined)[]): string | null {
  let latestValue: string | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (!value) {
      continue;
    }
    const time = Date.parse(value);
    if (Number.isNaN(time) || time <= latestTime) {
      continue;
    }
    latestValue = value;
    latestTime = time;
  }
  return latestValue;
}

function isStatusPageNotification(item: IncidentNotificationAttempt): boolean {
  return item.channel === IncidentNotificationChannel.StatusPage || item.audienceRef === INCIDENT_NOTIFICATION_AUDIENCE_REF.StatusPage;
}

function isOwnerAudienceRef(audienceRef: string): boolean {
  return audienceRef === INCIDENT_NOTIFICATION_AUDIENCE_REF.Owner || audienceRef.startsWith(INCIDENT_NOTIFICATION_AUDIENCE_REF.OwnerPrefix);
}

function outageNoticeTone(value: IncidentDetail["communicationSummary"]["outageNoticeState"]): UiTone {
  return OUTAGE_NOTICE_READY_STATES.has(value) ? UI_TONE.Ok : UI_TONE.Warn;
}

interface CustomerSegmentProjection {
  readonly id: string;
  readonly routeRef: string;
  readonly label: string;
  readonly health: GraphNode["health"];
  readonly summary: string;
  readonly badge: string;
  readonly actionCount: number;
}

function customersForRoute(detail: IncidentDetail, routeRef: string): readonly IncidentDetail["customerImpact"][number][] {
  return detail.customerImpact.filter((customer) => customer.affectedRouteRef === routeRef);
}

function customerSegmentRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly CustomerSegmentProjection[] {
  const groups = new Map<string, IncidentDetail["customerImpact"][number][]>();
  for (const customer of detail.customerImpact) {
    const key = `${customer.affectedRouteRef}:${customer.tier}`;
    const group = groups.get(key);
    if (group) {
      group.push(customer);
    } else {
      groups.set(key, [customer]);
    }
  }
  return [...groups.entries()].map(([key, customers]) => {
    const routeRef = customers[0]?.affectedRouteRef ?? "";
    const tier = customers[0]?.tier ?? "";
    const complaintCount = customers.reduce((sum, customer) => sum + customer.complaintCount, 0);
    const health = customerSegmentHealth(customers);
    return {
      id: graphNodeId(GRAPH_NODE_ID_PREFIX.CustomerSegment, key),
      routeRef,
      label: `${statusLabel(copy, tier)} ${copy.labels.customerCohort}`,
      health,
      summary: `${customers.length} ${copy.labels.impacted}; ${complaintCount} ${copy.labels.complaints}; ${copy.labels.resolutionReceipt}: ${summarizeValues(customers.map((customer) => statusLabel(copy, customer.resolutionNotificationState)), copy)}`,
      badge: summarizeValues(customers.map((customer) => statusLabel(copy, customer.state)), copy),
      actionCount: complaintCount
    };
  });
}

function customerSegmentHealth(customers: readonly IncidentDetail["customerImpact"][number][]): GraphNode["health"] {
  if (customers.some((customer) => customer.state === CUSTOMER_IMPACT_STATE.Impacted || customer.resolutionNotificationState === IncidentNotificationState.Failed)) {
    return GRAPH_HEALTH.Down;
  }
  if (customers.some((customer) => customer.state === CUSTOMER_IMPACT_STATE.Monitoring || customer.resolutionNotificationState === IncidentNotificationState.Queued)) {
    return GRAPH_HEALTH.Degraded;
  }
  return customers.length ? GRAPH_HEALTH.Healthy : GRAPH_HEALTH.Unknown;
}

function routeSummary(detail: IncidentDetail, routeRef: string, copy: OpsCenterCopy): string {
  const customers = customersForRoute(detail, routeRef);
  const complaintCount = customers.reduce((sum, customer) => sum + customer.complaintCount, 0);
  return `${customers.length} ${copy.labels.impacted}; ${complaintCount} ${copy.labels.complaints}`;
}

function routeHealth(detail: IncidentDetail, routeRef: string, fallback: GraphNode["health"]): GraphNode["health"] {
  const customers = customersForRoute(detail, routeRef);
  if (customers.some((customer) => customer.state === CUSTOMER_IMPACT_STATE.Impacted || customer.resolutionNotificationState === IncidentNotificationState.Failed)) {
    return GRAPH_HEALTH.Down;
  }
  if (customers.some((customer) => customer.state === CUSTOMER_IMPACT_STATE.Monitoring || customer.resolutionNotificationState === IncidentNotificationState.Queued)) {
    return GRAPH_HEALTH.Degraded;
  }
  return customers.length ? GRAPH_HEALTH.Healthy : fallback;
}

function providerRefForAction(actionRef: string): string {
  return TOOL_PROVIDER_BY_TOOL_ID[actionRef] ?? actionRef;
}

function providerLabel(providerRef: string, copy: OpsCenterCopy): string {
  return copy.toolProviders[providerRef] ?? copy.toolLabels[providerRef] ?? humanize(providerRef);
}

function providerSummary(rows: readonly ActionRow[], providerRef: string, copy: OpsCenterCopy): string {
  const providerRows = rows.filter((row) => providerRefForAction(row.toolId ?? row.actionRef) === providerRef);
  if (!providerRows.length) {
    return copy.empty.noTypedActions;
  }
  return providerRows.map((row) => `${row.label}: ${row.summary}`).join(" / ");
}

function providerHealth(rows: readonly ActionRow[], providerRef: string): GraphNode["health"] {
  const providerRows = rows.filter((row) => providerRefForAction(row.toolId ?? row.actionRef) === providerRef);
  if (providerRows.some((row) => row.status === WORKFLOW_STATUS.Blocked)) {
    return GRAPH_HEALTH.Down;
  }
  if (providerRows.some((row) => row.requiresApproval)) {
    return GRAPH_HEALTH.Degraded;
  }
  return providerRows.length ? GRAPH_HEALTH.Healthy : GRAPH_HEALTH.Unknown;
}

export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

const GRAPH_NODE_ROLE_LABEL_KEY: Readonly<Record<string, string>> = {
  [GRAPH_NODE_KIND.Incident]: "graphRoleIncident",
  [GRAPH_NODE_KIND.Service]: "graphRoleService",
  [GRAPH_NODE_KIND.Route]: "graphRoleRoute",
  [GRAPH_NODE_KIND.Provider]: "graphRoleProvider",
  [GRAPH_NODE_KIND.CustomerSegment]: "graphRoleCustomerSegment",
  [GRAPH_NODE_KIND.Owner]: "graphRoleOwner"
};

const GRAPH_EDGE_LABEL_KEY: Readonly<Record<string, string>> = {
  [GRAPH_EDGE_LABEL.Observes]: "edgeObserves",
  [GRAPH_EDGE_LABEL.Routes]: "edgeRoutes",
  [GRAPH_EDGE_LABEL.Recovers]: "edgeRecovers",
  [GRAPH_EDGE_LABEL.Owns]: "edgeOwns",
  [GRAPH_EDGE_LABEL.Impacts]: "edgeImpacts"
};

function graphNode(kind: string, id: string, label: string, nodeKind: string, health: GraphNode["health"], column: number, row: number, summary: string, badge: string, actionCount: number, copy: OpsCenterCopy): GraphNode {
  return { kind, id, label, roleLabel: graphNodeRoleLabel(nodeKind, copy), nodeKind, health, column, row, summary, badge, actionCount };
}

function graphNodeId(prefix: string, value: string): string {
  return `${prefix}${value}`;
}

function graphEdgeId(value: string): string {
  return `${GRAPH_EDGE_ID_PREFIX}${value}`;
}

function graphNodeRoleLabel(nodeKind: string, copy: OpsCenterCopy): string {
  const labelKey = GRAPH_NODE_ROLE_LABEL_KEY[nodeKind];
  return labelKey ? copy.labels[labelKey] ?? humanize(nodeKind) : humanize(nodeKind);
}

function graphEdge(id: string, from: GraphNode, to: GraphNode, label: string, copy: OpsCenterCopy): GraphEdge {
  return {
    id,
    from: from.id,
    to: to.id,
    fromLabel: from.label,
    toLabel: to.label,
    label,
    displayLabel: graphEdgeDisplayLabel(label, copy),
    health: graphEdgeHealth(from, to),
    summary: `${from.label} -> ${to.label}`
  };
}

function graphEdgeDisplayLabel(label: string, copy: OpsCenterCopy): string {
  const labelKey = GRAPH_EDGE_LABEL_KEY[label];
  return labelKey ? copy.labels[labelKey] ?? humanize(label) : humanize(label);
}

function graphEdgeHealth(from: GraphNode, to: GraphNode): GraphNode["health"] {
  if (from.health === GRAPH_HEALTH.Down || to.health === GRAPH_HEALTH.Down) {
    return GRAPH_HEALTH.Down;
  }
  if (from.health === GRAPH_HEALTH.Degraded || to.health === GRAPH_HEALTH.Degraded) {
    return GRAPH_HEALTH.Degraded;
  }
  if (from.health === GRAPH_HEALTH.Healthy && to.health === GRAPH_HEALTH.Healthy) {
    return GRAPH_HEALTH.Healthy;
  }
  return GRAPH_HEALTH.Unknown;
}
